import { useEffect, useMemo, useState } from "react";
import { useSearch } from "wouter";
import { Download, RotateCw, Search, SearchX, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  OP_LABEL,
  STATUS_LABEL,
  fmt,
  fmtDate,
  signed,
  type Tx,
} from "@/types/dashboard";
import {
  EnvironmentBadge,
  Flag,
  KybLock,
  PageHead,
  StatusPill,
} from "@/components/bits";
import { TxDetailDialog } from "@/components/tx-detail-dialog";
import { useI18n } from "@/i18n/i18n-context";
import {
  Empty,
  ErrorBox,
  Pager,
  SkeletonRows,
  buildQuery,
  pageInfo,
  rows as toRows,
  useFinanceGate,
  useServer,
} from "@/features/finance/kit";
import { toTx } from "@/features/finance/tx-map";

const PER_PAGE = 25;

function F({
  value,
  onChange,
  label,
  items,
  testid,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  items: [string, string][];
  testid: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-label={label}
        data-testid={testid}
        className={cn(
          "h-9 w-full bg-card sm:w-auto sm:min-w-36",
          value !== "all" && "border-primary/50 text-primary",
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label} : tous</SelectItem>
        {items.map(([v, l]) => (
          <SelectItem key={v} value={v}>
            {l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function Transactions() {
  const gate = useFinanceGate();
  const { toast } = useToast();
  const search = useSearch();
  const { t, lang, dict } = useI18n();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [currency, setCurrency] = useState("all");
  const [environment, setEnvironment] = useState("all");
  const [op, setOp] = useState("all");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<Tx | null>(null);

  useEffect(() => {
    const p = new URLSearchParams(search);
    setOp(p.get("op") ?? "all");
    setStatus(p.get("status") ?? "all");
    const qq = p.get("q") ?? "";
    setQ(qq);
    setDebounced(qq);
    setPage(1);
  }, [search]);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(
    () => setPage(1),
    [debounced, currency, environment, op, status, from, to],
  );

  const currencies = useMemo(
    () =>
      Array.from(
        new Set(gate.d.wallets.map((w) => w.currency).filter(Boolean)),
      ).sort(),
    [gate.d.wallets],
  );

  const path = gate.allowed
    ? buildQuery("/transactions", {
        currency,
        environment,
        operation: op,
        status,
        from,
        to,
        search: debounced,
        per_page: PER_PAGE,
        page,
      })
    : null;
  const list = useServer<unknown>(path);
  const raw = useMemo(() => toRows(list.data), [list.data]);
  const txs = useMemo(() => raw.map(toTx), [raw]);
  const info = pageInfo(list.data, list.meta, raw.length, PER_PAGE);

  const clear = () => {
    setQ("");
    setDebounced("");
    setCurrency("all");
    setEnvironment("all");
    setOp("all");
    setStatus("all");
    setFrom("");
    setTo("");
  };
  const active =
    q ||
    from ||
    to ||
    [currency, environment, op, status].some((v) => v !== "all");

  const exportCsv = () => {
    const head = [
      "reference",
      "date",
      "operation",
      "statut",
      "devise",
      "montant",
      "frais",
      "methode",
      "contrepartie",
      "libelle",
    ];
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = txs.map((t) =>
      [
        t.reference,
        t.createdAt,
        t.operation,
        t.status,
        t.currency,
        signed(t) * t.amount,
        t.fee,
        t.method,
        t.counterparty,
        t.description,
      ]
        .map(esc)
        .join(";"),
    );
    const label = `# Export BizConnectPay - page ${info.page}${info.last ? `/${info.last}` : ""} chargee depuis le serveur (${txs.length} lignes). Ce fichier ne contient PAS l'ensemble de l'historique.`;
    const blob = new Blob(
      ["\uFEFF" + label + "\n" + [head.join(";"), ...lines].join("\n")],
      { type: "text/csv;charset=utf-8" },
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bizconnectpay-transactions-page-${info.page}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast({
      title: "Export de la page courante",
      description: `${txs.length} lignes de la page ${info.page}. Les autres pages ne sont pas incluses.`,
    });
  };

  if (gate.locked || (!gate.loading && !gate.allowed))
    return (
      <>
        <PageHead title="Transactions" />
        <KybLock what="L'historique des transactions" />
      </>
    );

  return (
    <>
      <PageHead title={t("transactions.title")} sub={t("transactions.sub")}>
        <EnvironmentBadge />
        <Button
          variant="outline"
          onClick={list.reload}
          disabled={list.loading}
          className="gap-1.5"
          data-testid="button-refresh-tx"
        >
          <RotateCw className={cn("h-4 w-4", list.loading && "animate-spin")} />{" "}
          {t("common.refresh")}
        </Button>
        <Button
          variant="outline"
          onClick={exportCsv}
          disabled={!txs.length}
          data-testid="button-export-csv"
          className="gap-1.5"
          title={
            lang === "en"
              ? "Exports only displayed page"
              : "Exporte uniquement la page affichée"
          }
        >
          <Download className="h-4 w-4" />{" "}
          {lang === "en"
            ? `Export this page (${txs.length})`
            : `Exporter cette page (${txs.length})`}
        </Button>
      </PageHead>
      <div className="rise mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <label className="relative flex-1 sm:min-w-64">
          <span className="sr-only">{t("transactions.searchPlaceholder")}</span>
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="input-tx-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("transactions.searchPlaceholder")}
            className="h-9 bg-card pl-9"
          />
        </label>
        <F
          label={t("common.currency")}
          testid="filter-currency"
          value={currency}
          onChange={setCurrency}
          items={(currencies.length
            ? currencies
            : ["XAF", "XOF", "CDF", "USD"]
          ).map((c) => [c, c])}
        />
        <F
          label="Environnement"
          testid="filter-environment"
          value={environment}
          onChange={setEnvironment}
          items={[
            ["LIVE", "Live"],
            ["SANDBOX", "Sandbox"],
          ]}
        />
        <F
          label={t("common.type")}
          testid="filter-op"
          value={op}
          onChange={setOp}
          items={Object.entries(dict.operations as Record<string, string>)}
        />
        <F
          label={t("common.status")}
          testid="filter-status"
          value={status}
          onChange={setStatus}
          items={Object.entries(dict.statuses as Record<string, string>)}
        />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {lang === "en" ? "From" : "Du"}
          <Input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 w-auto bg-card"
            aria-label="Date de début"
            data-testid="filter-from"
          />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {lang === "en" ? "To" : "Au"}
          <Input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 w-auto bg-card"
            aria-label="Date de fin"
            data-testid="filter-to"
          />
        </label>
        {active && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clear}
            data-testid="button-clear-filters"
            className="h-9 gap-1"
          >
            <X className="h-4 w-4" /> {lang === "en" ? "Clear" : "Effacer"}
          </Button>
        )}
      </div>

      <div className="rise overflow-hidden rounded-xl border bg-card">
        {list.error ? (
          <ErrorBox className="m-4" error={list.error} onRetry={list.reload} />
        ) : list.loading && !txs.length ? (
          <SkeletonRows n={7} />
        ) : txs.length === 0 ? (
          <div data-testid="empty-transactions">
            <Empty
              icon={<SearchX className="h-5 w-5" />}
              title={
                active
                  ? t("transactions.emptyTransactions")
                  : lang === "en"
                    ? "No transactions yet"
                    : "Aucune transaction pour le moment"
              }
            >
              {active ? (
                <Button variant="outline" size="sm" onClick={clear}>
                  {lang === "en"
                    ? "Reset filters"
                    : "Réinitialiser les filtres"}
                </Button>
              ) : lang === "en" ? (
                "Your deposits, withdrawals, and transfers will appear here."
              ) : (
                "Vos dépôts, retraits et transferts apparaîtront ici."
              )}
            </Empty>
          </div>
        ) : (
          <div
            className={cn(
              "overflow-x-auto transition-opacity",
              list.loading && "opacity-60",
            )}
          >
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/60 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">
                    {t("common.date")}
                  </th>
                  <th className="px-4 font-semibold">
                    {t("common.reference")}
                  </th>
                  <th className="px-4 font-semibold">{t("common.type")}</th>
                  <th className="px-4 font-semibold">
                    {lang === "en" ? "Method / Phone" : "Méthode / Numéro"}
                  </th>
                  <th className="px-4 font-semibold">Wallet</th>
                  <th className="px-4 font-semibold">{t("common.status")}</th>
                  <th className="px-4 text-right font-semibold">
                    {t("common.amount")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {txs.map((tTx) => (
                  <tr
                    key={tTx.reference}
                    data-testid={`row-tx-${tTx.reference}`}
                    tabIndex={0}
                    onClick={() => setDetail(tTx)}
                    onKeyDown={(e) => e.key === "Enter" && setDetail(tTx)}
                    className="cursor-pointer outline-none transition hover:bg-accent/40 focus-visible:bg-accent/60"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">
                      {tTx.createdAt ? fmtDate(tTx.createdAt) : "—"}
                    </td>
                    <td className="px-4 font-mono text-xs">{tTx.reference}</td>
                    <td className="px-4">
                      <p className="font-semibold">
                        {(dict.operations as Record<string, string>)[
                          tTx.operation
                        ] ?? OP_LABEL[tTx.operation]}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {tTx.description || "—"}
                      </p>
                    </td>
                    <td className="px-4">
                      <span className="inline-block rounded bg-muted px-1.5 py-0.5 text-[11px] font-semibold uppercase text-slate-800">
                        {tTx.method || "Mobile Money"}
                      </span>
                      <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                        {tTx.counterparty && tTx.counterparty !== "—"
                          ? tTx.counterparty
                          : "—"}
                      </p>
                    </td>
                    <td className="px-4">
                      <span className="flex items-center gap-2">
                        <Flag code={tTx.country} />
                        {tTx.currency}
                      </span>
                    </td>
                    <td className="px-4">
                      <StatusPill status={tTx.status} />
                    </td>
                    <td
                      className={cn(
                        "tnum whitespace-nowrap px-4 text-right font-bold",
                        signed(tTx) > 0 && "text-emerald-700",
                      )}
                    >
                      {signed(tTx) < 0 ? "−" : "+"}
                      {fmt(tTx.amount, tTx.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!list.error && (txs.length > 0 || page > 1) && (
          <Pager info={info} loading={list.loading} onPage={setPage} />
        )}
      </div>
      <TxDetailDialog tx={detail} onClose={() => setDetail(null)} />
    </>
  );
}
